import * as React from 'react'
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { getJSON } from '@/lib/api'
import { cn } from '@/lib/utils'

/** The paging fields a staff list adds to its response when `limit` or `cursor` was sent (#3283). */
export interface PagedResponse {
  limit?: number
  next_cursor?: string | null
  total_count?: number
}

/** Staff lists ask for this many rows at a time (#3284). */
export const STAFF_LIST_PAGE_SIZE = 25

export interface CursorPager {
  /** 1-based position of the first row on this page; 0 when the list is empty. */
  from: number
  to: number
  /** The server's count of every row matching the list's filters; absent when the list omits it. */
  total: number | undefined
  hasPrevious: boolean
  hasNext: boolean
  busy: boolean
  previous: () => void
  next: () => void
}

export function pagedPath(path: string, limit: number, cursor?: string) {
  const separator = path.includes('?') ? '&' : '?'
  const cursorParam = cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''
  return `${path}${separator}limit=${limit}${cursorParam}`
}

/**
 * Walks a cursor-paged staff list: `path` carries every filter and sort, so a change to any of
 * them starts again from the first page before the next fetch. The cursors already walked make
 * Previous possible without the server having to issue one. The rows on screen stay put while
 * the next page loads, so the pager and the toolbar never unmount under the pointer; the range
 * shown follows the rows, not the request.
 */
export function useCursorPages<TResponse extends PagedResponse>({
  queryKey,
  path,
  limit,
}: {
  queryKey: readonly unknown[]
  path: string
  limit: number
}): { query: UseQueryResult<TResponse>; pager: CursorPager | null } {
  const [position, setPosition] = React.useState<{ path: string; cursors: string[] }>({
    path,
    cursors: [],
  })
  React.useEffect(() => setPosition({ path, cursors: [] }), [path])
  const cursors = position.path === path ? position.cursors : []
  const cursor = cursors.at(-1)
  const query = useQuery({
    queryKey: [...queryKey, path, cursor],
    queryFn: () => getJSON<TResponse>(pagedPath(path, limit, cursor)),
    retry: false,
    placeholderData: keepPreviousData,
  })
  // The page whose rows are on screen; it lags `cursors` while the next page is in flight.
  const [shownPage, setShownPage] = React.useState(0)
  const fresh = query.isSuccess && !query.isPlaceholderData
  React.useEffect(() => {
    if (fresh) setShownPage(cursors.length)
  }, [fresh, cursors.length])
  const move = (values: string[]) => setPosition({ path, cursors: values })
  return { query, pager: cursorPager(query, { cursors, shownPage, limit }, move) }
}

function cursorPager<TResponse extends PagedResponse>(
  query: UseQueryResult<TResponse>,
  { cursors, shownPage, limit }: { cursors: string[]; shownPage: number; limit: number },
  move: (cursors: string[]) => void,
): CursorPager | null {
  const data = query.data
  if (!data || (data.total_count === undefined && data.next_cursor === undefined)) return null
  const pageSize = data.limit ?? limit
  const total = data.total_count
  const first = shownPage * pageSize + 1
  const next = data.next_cursor
  const settled = !query.isFetching
  return {
    from: total === undefined ? first : Math.min(first, total),
    to: total === undefined ? first + pageSize - 1 : Math.min(first + pageSize - 1, total),
    total,
    hasPrevious: cursors.length > 0,
    hasNext: Boolean(next),
    busy: query.isFetching,
    previous: () => {
      if (cursors.length > 0 && settled) move(cursors.slice(0, -1))
    },
    next: () => {
      if (next && settled) move([...cursors, next])
    },
  }
}

/** A list's description line, from the server total: `4,212 Contacts`, `37 matching Contacts`. */
export function totalDescription(
  total: number | undefined,
  one: string,
  many: string,
  filtered: boolean,
) {
  if (total === undefined) return undefined
  return `${formatCount(total)} ${filtered ? 'matching ' : ''}${total === 1 ? one : many}`
}

function formatCount(value: number) {
  return value.toLocaleString('en-GB')
}

/**
 * The staff-list pager: the range on show and Previous / Next, rendered once above and once
 * below the table inside the list's card. Nothing renders for an unpaged or empty list.
 */
export function ListPager({
  pager,
  label,
  position,
}: {
  pager: CursorPager | null
  label: string
  position: 'top' | 'bottom'
}) {
  if (!pager || pager.total === undefined || pager.total === 0) return null
  return (
    <Pagination
      aria-label={`${label} pages ${position === 'top' ? 'above' : 'below'} the table`}
      aria-busy={pager.busy}
      className={cn(
        'flex-wrap items-center justify-between gap-3 px-4 py-2',
        position === 'top' ? 'border-b border-border' : 'border-t border-border',
      )}
    >
      <p className="text-body-sm text-muted-foreground">
        Showing {formatCount(pager.from)} to {formatCount(pager.to)} of {formatCount(pager.total)}
      </p>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious {...pagerLink(pager.hasPrevious && !pager.busy, pager.previous)} />
        </PaginationItem>
        <PaginationItem>
          <PaginationNext {...pagerLink(pager.hasNext && !pager.busy, pager.next)} />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}

/** A pager link stays in the document when it cannot move, so the control set never shifts. */
function pagerLink(enabled: boolean, move: () => void): React.ComponentProps<'a'> {
  return {
    href: '#',
    'aria-disabled': !enabled,
    tabIndex: enabled ? undefined : -1,
    className: enabled ? undefined : 'pointer-events-none opacity-50',
    onClick: (event) => {
      event.preventDefault()
      if (enabled) move()
    },
  }
}
